import React from "react";
import { ArrowRight } from "lucide-react";
import Badge from "../ui/Badge";
import Card from "../ui/Card";

const FeaturesSection = ({ keyFeatures }) => {
  return (
    <section
      id="features"
      className="py-20 sm:py-28 px-4 sm:px-6 relative overflow-hidden"
    >
      {/* Unique Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Enhanced Header Design */}
        <div className="mb-16 sm:mb-20 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-teal-500/10 via-rose-500/10 to-orange-500/10 border border-teal-500/20 mb-8">
            <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
            <span className="text-xs sm:text-sm font-bold text-teal-400 uppercase tracking-widest">
              Core Features
            </span>
            <div
              className="w-2 h-2 rounded-full bg-rose-400 animate-pulse"
              style={{ animationDelay: "0.2s" }}
            ></div>
          </div>
          <h2 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-white mb-6 leading-[0.95]">
            <span className="block">Powerful Tools for</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-orange-500 to-teal-500 animate-gradient">
              Modern Creators
            </span>
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-zinc-300 max-w-3xl mx-auto leading-relaxed font-light">
            Everything you need to discover your personality through AI analysis.
            <span className="block mt-2 text-teal-400/80">
              Built for accuracy, designed for privacy.
            </span>
          </p>
        </div>

        {/* Clean 3-Column Grid Layout */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {keyFeatures.map((feature, idx) => (
            <div key={idx} className="group relative">
              <div className="h-full relative">
                {/* Unique Card Design */}
                <div className="absolute inset-0 bg-gradient-to-br from-stone-900/50 to-stone-950/50 rounded-2xl border border-stone-800/50 group-hover:border-teal-500/30 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-teal-500/10"></div>

                {/* Animated Border on Hover */}
                <div className="absolute -inset-px bg-gradient-to-r from-teal-500/0 via-rose-500/0 to-orange-500/0 group-hover:from-teal-500/20 group-hover:via-rose-500/20 group-hover:to-orange-500/20 rounded-2xl blur-sm transition-all duration-500 opacity-0 group-hover:opacity-100"></div>

                <div className="relative p-6 sm:p-8 h-full flex flex-col">
                  {/* Enhanced Icon Section */}
                  <div className="mb-6">
                    <div className="relative inline-flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-stone-900/80 to-stone-950/80 border-2 border-stone-800/50 group-hover:border-teal-500/50 group-hover:shadow-lg group-hover:shadow-teal-500/20 transition-all duration-300">
                      {/* Glow effect on hover */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-teal-500/0 to-rose-500/0 group-hover:from-teal-500/20 group-hover:to-rose-500/20 rounded-2xl blur-md transition-all duration-300 opacity-0 group-hover:opacity-100"></div>

                      <div className="relative p-3 rounded-xl bg-gradient-to-br from-teal-500/10 via-rose-500/10 to-orange-500/10 group-hover:from-teal-500/20 group-hover:via-rose-500/20 group-hover:to-orange-500/20 transition-all duration-300 group-hover:scale-110">
                        {feature.iconComponent}
                      </div>
                      <span className="text-4xl group-hover:scale-125 transition-transform duration-300 relative z-10">
                        {feature.image}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-rose-400 group-hover:to-teal-400 transition-all duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-6 flex-1 group-hover:text-zinc-200 transition-colors">
                    {feature.desc}
                  </p>

                  {feature.cta && (
                    <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-teal-500/10 to-rose-500/10 border border-teal-500/20 text-teal-400 text-sm font-bold group-hover:from-teal-500/20 group-hover:to-rose-500/20 group-hover:border-teal-500/40 group-hover:text-teal-300 transition-all duration-300 cursor-pointer w-fit">
                      <span>{feature.cta}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;

